const NORMALIZE_SPACE = /\s+/g;
const STRIP_EMOJI = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu;
const MAX_TOOLTIP_LEN = 72;

const SHORT_HINTS = [
  { key: "login", text: "Open login page" },
  { key: "register", text: "Open account registration" },
  { key: "upload", text: "Upload selected file" },
  { key: "download", text: "Download this file" },
  { key: "view main file", text: "Open the main document" },
  { key: "view", text: "Open and view details" },
  { key: "preview", text: "Preview before final action" },
  { key: "search", text: "Search records" },
  { key: "approve", text: "Approve and move forward" },
  { key: "review", text: "Return for correction" },
  { key: "archive", text: "Move file to archive" },
  { key: "delete", text: "Delete this item permanently" },
  { key: "save", text: "Save current changes" },
  { key: "close", text: "Close this panel" },
];

const getActionVerb = (text) => {
  const t = String(text || "").toLowerCase();
  if (t.includes("login") || t.includes("log in")) return "Log in";
  if (t.includes("register") || t.includes("sign up")) return "Register";
  if (t.includes("upload")) return "Upload";
  if (t.includes("download")) return "Download";
  if (t.includes("view")) return "View";
  if (t.includes("preview")) return "Preview";
  if (t.includes("search")) return "Search";
  if (t.includes("archive")) return "Archive";
  if (t.includes("approve")) return "Approve";
  if (t.includes("review")) return "Review";
  if (t.includes("delete") || t.includes("remove")) return "Delete";
  if (t.includes("save")) return "Save";
  if (t.includes("close")) return "Close";
  return "Use";
};

const deriveHelpText = (el) => {
  const explicit = el.getAttribute("data-assist");
  if (explicit) return explicit.trim();

  const aria = el.getAttribute("aria-label");
  if (aria) return aria.trim();

  const title = el.getAttribute("title");
  if (title) return title.trim();

  const raw = (el.textContent || "")
    .replace(STRIP_EMOJI, "")
    .replace(NORMALIZE_SPACE, " ")
    .trim();
  if (!raw) return "Use this control";

  const lower = raw.toLowerCase();
  const hint = SHORT_HINTS.find((h) => lower.includes(h.key));
  if (hint) return hint.text;

  const verb = getActionVerb(raw);
  const line = `${verb} ${raw}`;
  if (line.length <= MAX_TOOLTIP_LEN) return line;
  return `${line.slice(0, MAX_TOOLTIP_LEN - 1)}…`;
};

export const initAssistiveTooltips = () => {
  if (typeof document === "undefined") return () => {};

  const selector = [
    "button",
    "a.btn",
    "a[role='button']",
    "input[type='button']",
    "input[type='submit']",
    "[data-assist]",
  ].join(", ");

  const tooltip = document.createElement("div");
  tooltip.className = "assistive-tooltip";
  tooltip.setAttribute("role", "tooltip");
  tooltip.setAttribute("aria-hidden", "true");
  document.body.appendChild(tooltip);

  let activeEl = null;

  const positionTooltip = (event) => {
    const offset = 14;
    tooltip.style.left = `${event.clientX + offset}px`;
    tooltip.style.top = `${event.clientY + offset}px`;
  };

  const show = (el, event) => {
    const text = deriveHelpText(el);
    if (!text) return;
    activeEl = el;
    tooltip.textContent = text;
    tooltip.setAttribute("aria-hidden", "false");
    tooltip.classList.add("is-visible");
    if (event) positionTooltip(event);
  };

  const hide = () => {
    activeEl = null;
    tooltip.classList.remove("is-visible");
    tooltip.setAttribute("aria-hidden", "true");
  };

  const onMouseOver = (event) => {
    const el = event.target?.closest?.(selector);
    if (!el) return;
    show(el, event);
  };

  const onMouseMove = (event) => {
    if (!activeEl) return;
    positionTooltip(event);
  };

  const onMouseOut = (event) => {
    if (!activeEl) return;
    const next = event.relatedTarget;
    if (next && next.closest && next.closest(selector) === activeEl) return;
    hide();
  };

  const onScrollOrResize = () => {
    if (activeEl) hide();
  };

  document.addEventListener("mouseover", onMouseOver, true);
  document.addEventListener("mousemove", onMouseMove, true);
  document.addEventListener("mouseout", onMouseOut, true);
  window.addEventListener("scroll", onScrollOrResize, true);
  window.addEventListener("resize", onScrollOrResize, true);

  return () => {
    document.removeEventListener("mouseover", onMouseOver, true);
    document.removeEventListener("mousemove", onMouseMove, true);
    document.removeEventListener("mouseout", onMouseOut, true);
    window.removeEventListener("scroll", onScrollOrResize, true);
    window.removeEventListener("resize", onScrollOrResize, true);
    tooltip.remove();
  };
};
